import { fetchAndActivate, getValue } from "firebase/remote-config";
import { remoteConfig } from "@/firebase";
import {
  defaultMessages,
  type LocaleCatalog,
  type LocaleMessages,
} from "./defaultMessages";
import { SUPPORTED_LOCALES } from "./locale";
import {
  I18N_NAMESPACES_REMOTE_CONFIG_KEY,
  readDynamicRemoteConfigKeys,
} from "./remoteConfigKeys";
import { decodeHtmlEntities } from "./htmlEntities";

const I18N_REMOTE_CONFIG_KEYS = [
  "i18n_common_v1",
  "i18n_settings_v1",
  "i18n_data_chat_v1",
  "i18n_app_v1",
] as const;

type RemoteCatalogShape =
  | Partial<LocaleCatalog>
  | {
      messages?: Partial<LocaleCatalog>;
    };

export async function loadRemoteMessages(): Promise<Partial<LocaleCatalog>> {
  await fetchAndActivate(remoteConfig);
  const keys = [
    ...I18N_REMOTE_CONFIG_KEYS,
    ...readDynamicRemoteConfigKeys(
      getValue(remoteConfig, I18N_NAMESPACES_REMOTE_CONFIG_KEY).asString(),
      I18N_REMOTE_CONFIG_KEYS,
    ),
  ];

  return keys.reduce<Partial<LocaleCatalog>>((catalog, key) => {
    const rawValue = getValue(remoteConfig, key).asString();
    const parsed = parseRemoteCatalog(rawValue);

    for (const locale of SUPPORTED_LOCALES) {
      catalog[locale] = {
        ...(catalog[locale] || {}),
        ...(parsed[locale] || {}),
      };
    }

    return catalog;
  }, {});
}

export function mergeMessages(
  remoteCatalog: Partial<LocaleCatalog> = {},
): LocaleCatalog {
  return {
    en: {
      ...defaultMessages.en,
      ...cleanMessages(remoteCatalog.en),
    },
    uk: {
      ...defaultMessages.uk,
      ...cleanMessages(remoteCatalog.uk),
    },
  };
}

function parseRemoteCatalog(rawValue: string): Partial<LocaleCatalog> {
  if (!rawValue.trim()) return {};

  try {
    const parsed = JSON.parse(rawValue) as RemoteCatalogShape;
    const source = (
      "messages" in parsed && parsed.messages ? parsed.messages : parsed
    ) as Partial<LocaleCatalog>;
    return {
      en: cleanMessages(source.en),
      uk: cleanMessages(source.uk),
    };
  } catch {
    return {};
  }
}

function cleanMessages(messages: unknown): LocaleMessages {
  if (!messages || typeof messages !== "object" || Array.isArray(messages)) {
    return {};
  }

  return Object.entries(
    messages as Record<string, unknown>,
  ).reduce<LocaleMessages>((result, [key, value]) => {
    if (typeof value === "string") {
      result[key] = decodeHtmlEntities(value);
    }
    return result;
  }, {});
}
